import { useLocation, Navigate } from "react-router-dom";
import { appConfig } from "../appConfig";
import { useGetCurrentUserQuery } from "../features/identity/identityApi";
import BoniboLoader from "../components/loaders/BoniboLoader";
import { useCallback, useMemo } from "react";

type RequireAuthProps = {
  children: JSX.Element;
  policies?: Array<string>;
  some?: boolean;
  redirect?: boolean;
};

export const RequireAuth = ({ children, policies, some, redirect = false }: RequireAuthProps) => {
  let location = useLocation();
  const { data: currentUser, isLoading, isUninitialized, isFetching } = useGetCurrentUserQuery();

  const checkPolicy = useCallback(
    (policy: string) => {
      if (!currentUser?.claims) return false;

      const [claimKey, claimValue] = policy.split("-");
      return currentUser.claims[claimKey]?.includes(claimValue);
    },
    [currentUser?.claims]
  );

  const hasPermissions = useMemo(() => {
    if (!policies?.length) return true;
    if (!currentUser?.claims) return false;

    return some ? policies.some(checkPolicy) : policies.every(checkPolicy);
  }, [checkPolicy, currentUser?.claims, policies, some]);

  if (isLoading || isUninitialized || isFetching) {
    return <BoniboLoader />;
  }

  if (!currentUser || !hasPermissions) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    if (!redirect) {
      return null;
    }

    return <Navigate to={appConfig.applicationPaths.login} state={{ from: location }} replace />;
  }

  return children;
};
